/**
 * ObjectTypeNode — the React Flow port of the Rappid `merode.ObjectType` shape
 * (phase 6b).
 *
 * Pixel-faithful re-implementation of `src/lib/joint/shapes.js`
 * (`merode.ObjectType`) + the per-instance attrs from
 * `src/lib/joint/factories.js:createEDGNode` and the prop-sync logic in
 * `src/components/EDG/object_type.js`. The exact geometry the old JointJS shape
 * produced:
 *
 *  - rect: white fill, black stroke, stroke-width 2, sized from the uimodel node
 *    (default 100x50).
 *  - name text: Arial 14px, fill #000000, centred (ref-x 0.5, text-anchor
 *    middle, y-alignment middle). In UML notation an ABSTRACT object renders the
 *    name italic and lifted to ref-y 0.3 (instead of 0.5).
 *  - level text: Arial 12px, fill #000000, at ref-x 0.9 / ref-y 0.1
 *    (text-anchor 'right', y-alignment 'top'). Only shown when the EDG `show_bul`
 *    / `show_tdl` tool is active (the caller passes the resolved level string).
 *  - stereotype text `{abstract}`: Arial 12px, ref-x 0.5 / ref-y 0.8,
 *    text-anchor middle, y-alignment bottom. Only in UML notation for abstract
 *    objects.
 *
 * JointJS ref-x/ref-y are FRACTIONS of the rect's bounding box (0..1). We
 * reproduce them by multiplying by the node width/height. `y-alignment`
 * ('middle' / 'top' / 'bottom') maps to SVG `dominant-baseline`; JointJS
 * `text-anchor: 'right'` is not a valid SVG value and JointJS treated the
 * unknown value as the default (`start`), so we use `start` for the level to
 * match the old rendering.
 *
 * React Flow sizes the node wrapper from the `width`/`height` it receives; we
 * draw an SVG that exactly fills the node box so the stroke and text land on the
 * same pixels the Rappid paper produced. The 2px stroke is centred on the rect
 * edge in both implementations (SVG default), so no inset is needed — but we
 * give the SVG a small overflow allowance so the outer 1px of stroke is not
 * clipped, matching JointJS which never clipped the element body.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React from 'react';
import {
  Handle,
  NodeResizer,
  NodeResizeControl,
  Position,
  ResizeControlVariant,
  type NodeProps,
  type Node,
} from '@xyflow/react';

export type Notation = 'MERODE' | 'UML';

/**
 * Minimum node size for the resizer, ported from the JointJS FreeTransform world.
 * The Rappid `FreeTransform` `minWidth`/`minHeight` defaulted to 1, but the EDG
 * node default is 100x50 and shrinking below the label is never useful; the old
 * paper effectively never produced sub-label rects. We pick a small, safe floor
 * that keeps the label readable while staying well under the default size so a
 * resize-smaller gesture still works. Aspect ratio is FREE (FreeTransform had no
 * `preserveAspectRatio`), matching the 8-independent-handle behaviour.
 */
export const NODE_MIN_WIDTH = 40;
export const NODE_MIN_HEIGHT = 30;

/**
 * Highlight class applied to the node group, mirroring the JointJS
 * `joint-highlighted*` classes (driven by the four observable id-lists on the
 * uimodel/EDG store). null = no highlight. The CSS lives in
 * `src/diagram/edgFlow.scss` (ported from `components/EDG/styles.scss`).
 */
export type HighlightClass =
  | 'joint-highlighted'
  | 'joint-highlighted-path'
  | 'joint-highlighted-primary'
  | 'joint-highlighted-secondary'
  | null;

export type ObjectTypeNodeData = {
  /** Object type name (rect centre label). */
  name: string;
  /** Is the object type abstract? Drives the UML italic + `{abstract}`. */
  abstract: boolean;
  /** Active notation. */
  notation: Notation;
  /** Resolved level string (BUL/TDL), or undefined when no level tool active. */
  level?: string;
  /** Node width in px (from the uimodel node size). */
  width: number;
  /** Node height in px (from the uimodel node size). */
  height: number;
  /** Highlight class for this node, or null. */
  highlight: HighlightClass;
  /**
   * Commit the final size (in flow coordinates) when a resize gesture ends.
   * Wired by EdgFlow → DevEdg/ProjectVersion to the existing `updateEDGSize`
   * command so undo/autosave work. Absent in render-only (6b) mode.
   */
  onResizeEnd?: (width: number, height: number) => void;
  /** Whether the node may be resized (interactive mode + the selected node). */
  resizable?: boolean;
  /**
   * Whether the node is a live link endpoint (a connect tool is armed). When
   * true the structural handles become full-node CONNECT handles so a
   * node-to-node drag creates a dependency / inheritance (the Rappid full-node
   * magnet). When false they stay inert (selection / drag mode).
   */
  connectable?: boolean;
};

/** React Flow node type alias for an ObjectType node. */
export type ObjectTypeFlowNode = Node<ObjectTypeNodeData, 'objectType'>;

const FONT_FAMILY = 'Arial, helvetica, sans-serif';
const STROKE_WIDTH = 2;

/**
 * Structural-only handle style: invisible, zero-size, centred, non-interactive.
 * Present solely so React Flow's `getEdgePosition` resolves (edges are dropped
 * without a source+target handle); the visible geometry is our own.
 */
const HIDDEN_HANDLE_STYLE: React.CSSProperties = {
  width: 1,
  height: 1,
  minWidth: 0,
  minHeight: 0,
  background: 'transparent',
  border: 'none',
  opacity: 0,
  pointerEvents: 'none',
  // Centre the handle so any stray default rendering can't shift layout.
  left: '50%',
  top: '50%',
};

/**
 * Full-node CONNECT handle style: covers the entire node so a node-to-node drag
 * starts/lands anywhere on it (the Rappid full-node magnet). Invisible — only the
 * cursor signals connectability. Used only while a connect tool is armed; the
 * `react-flow__handle` reset in edgFlow.scss keeps it borderless/transparent.
 */
const CONNECT_HANDLE_STYLE: React.CSSProperties = {
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  transform: 'none',
  borderRadius: 0,
  background: 'transparent',
  border: 'none',
  opacity: 0,
};

function ObjectTypeNode({ data, selected }: NodeProps<ObjectTypeFlowNode>) {
  const {
    name,
    abstract: isAbstract,
    notation,
    level,
    width,
    height,
    highlight,
    onResizeEnd,
    resizable,
    connectable,
  } = data;
  const styleAsAbstract = isAbstract && notation === 'UML';

  // Transient size while a resize gesture is in flight. React Flow drives the
  // node wrapper size live during the drag, but the SVG body reads `width`/
  // `height` from `data` (which only updates once the committed MST size flows
  // back through deriveNodes). We mirror the live size locally so the rect +
  // text follow the handles, then commit on resize END through `onResizeEnd`
  // (the existing `updateEDGSize` command) so undo/autosave work — never during
  // the drag (the commit-on-drag-end pattern from the phase-6 plan).
  const [liveSize, setLiveSize] = React.useState<{ width: number; height: number } | null>(null);
  const drawW = liveSize ? liveSize.width : width;
  const drawH = liveSize ? liveSize.height : height;

  // JointJS ref-x/ref-y are fractions of the bounding box.
  const nameRefY = styleAsAbstract ? 0.3 : 0.5;

  // The SVG is the size of the node; a tiny overflow keeps the 1px outer half of
  // the 2px stroke from being clipped (JointJS never clipped the body).
  const groupClass = ['merode-object-type', highlight].filter(Boolean).join(' ');

  const showResizer = resizable === true && selected === true;

  // Shared resize handlers (corners, edge-lines, and the explicit midpoint
  // handles all use these). Live size mirrors the drag; commit on END only.
  const handleResize = React.useCallback(
    (_evt: unknown, params: { width: number; height: number }) =>
      setLiveSize({ width: params.width, height: params.height }),
    []
  );
  const handleResizeEnd = React.useCallback(
    (_evt: unknown, params: { width: number; height: number }) => {
      setLiveSize(null);
      if (onResizeEnd) onResizeEnd(params.width, params.height);
    },
    [onResizeEnd]
  );

  return (
    <>
      {/*
        FreeTransform parity: only the SELECTED node shows resize handles, and
        only when interactive (the old EDG enabled FreeTransform on the selected
        cell, `resizeable = activeTool === null`). NodeResizer draws the dashed
        box + the 4 CORNER handles; the four <NodeResizeControl> below add the 4
        EDGE-MIDPOINT handles, so the full 8-handle Rappid FreeTransform affordance
        is reproduced (baseline §6/§8). Rotation is disabled (no rotate handle).
        The orange look is styled in edgFlow.scss (`.react-flow__resize-control`).
        `keepAspectRatio` is OFF (FreeTransform had none). Live size during the
        drag; commit on END through `onResizeEnd` (the existing updateEDGSize).
      */}
      {showResizer ? (
        <>
          <NodeResizer
            isVisible
            minWidth={NODE_MIN_WIDTH}
            minHeight={NODE_MIN_HEIGHT}
            keepAspectRatio={false}
            onResize={handleResize}
            onResizeEnd={handleResizeEnd}
          />
          {(['top', 'right', 'bottom', 'left'] as const).map((pos) => (
            <NodeResizeControl
              key={pos}
              position={pos}
              variant={ResizeControlVariant.Handle}
              minWidth={NODE_MIN_WIDTH}
              minHeight={NODE_MIN_HEIGHT}
              keepAspectRatio={false}
              onResize={handleResize}
              onResizeEnd={handleResizeEnd}
            />
          ))}
        </>
      ) : null}
      {/*
        Floating-edge pattern: React Flow only computes edge endpoints (and thus
        renders an edge at all) when BOTH connected nodes expose a source and a
        target handle — `getEdgePosition` returns null otherwise and the edge is
        dropped. The actual endpoints are computed by our own boundary geometry
        (edgeGeometry.ts) and the custom edges draw from a precomputed `d`.

        Two modes:
         - NOT connect-armed: the handles are purely structural — centred,
           zero-size, non-interactive, transparent. They keep existing edges
           rendering without accepting new connections (selection / drag mode).
         - connect-armed (`connectable`): the same source+target handles become
           FULL-NODE connect handles (CONNECT_HANDLE_STYLE) so a node-to-node drag
           creates a link from/to anywhere on the node, like the Rappid magnet.
           ConnectionMode.Loose (set on the flow) lets the single source+target
           pair accept a drag in both directions.
      */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={connectable === true}
        className={connectable ? 'merode-connect-handle' : undefined}
        style={connectable ? CONNECT_HANDLE_STYLE : HIDDEN_HANDLE_STYLE}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={connectable === true}
        className={connectable ? 'merode-connect-handle' : undefined}
        style={connectable ? CONNECT_HANDLE_STYLE : HIDDEN_HANDLE_STYLE}
      />
      <svg
        className={groupClass}
        width={drawW}
        height={drawH}
        // overflow visible so the centred 2px stroke is fully painted
        style={{ overflow: 'visible', display: 'block' }}
      >
      <rect
        x={0}
        y={0}
        width={drawW}
        height={drawH}
        fill="white"
        stroke="black"
        strokeWidth={STROKE_WIDTH}
      />
      {/* name: centred (ref-x 0.5), Arial 14px, italic+lifted for UML abstract */}
      <text
        className="merode-object-type-name"
        x={drawW * 0.5}
        y={drawH * nameRefY}
        fill="#000000"
        fontSize={14}
        fontFamily={FONT_FAMILY}
        fontStyle={styleAsAbstract ? 'italic' : 'normal'}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {name}
      </text>
      {/* level: ref-x 0.9 / ref-y 0.1, Arial 12px, top-aligned, anchored start */}
      {level != null && level !== '' ? (
        <text
          className="merode-object-type-level"
          x={drawW * 0.9}
          y={drawH * 0.1}
          fill="#000000"
          fontSize={12}
          fontFamily={FONT_FAMILY}
          textAnchor="start"
          dominantBaseline="text-before-edge"
        >
          {level}
        </text>
      ) : null}
      {/* stereotype `{abstract}`: ref-x 0.5 / ref-y 0.8, bottom-aligned (UML only) */}
      {styleAsAbstract ? (
        <text
          className="merode-object-type-stereotype"
          x={drawW * 0.5}
          y={drawH * 0.8}
          fill="#000000"
          fontSize={12}
          fontFamily={FONT_FAMILY}
          textAnchor="middle"
          dominantBaseline="text-after-edge"
        >
          {'{abstract}'}
        </text>
      ) : null}
      </svg>
    </>
  );
}

export default ObjectTypeNode;
